import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { leaveService } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { format, differenceInDays } from 'date-fns';
import {
  Calendar as CalendarIcon,
  Plus,
  Check,
  X,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  FileText,
  Loader2,
  ChevronRight,
  UserCog,
  Upload,
  Download,
} from 'lucide-react';
import { employeeService, holidayService } from '../services/api';

const LEAVE_TYPES = [
  { value: 'casual', label: 'Casual Leave', color: 'blue' },
  { value: 'sick', label: 'Sick Leave', color: 'red' },
  { value: 'earned', label: 'Earned Leave', color: 'green' },
  { value: 'loss_of_pay', label: 'Loss of Pay', color: 'gray' },
  { value: 'comp_off', label: 'Comp Off', color: 'purple' },
];

const LEAVE_BALANCE_IMPORT_COLUMNS = [
  'employee_code',
  'earned_leave',
  'sick_leave',
  'comp_off',
  'reason',
];

export default function LeavesPage() {
  const { user, isAdmin, isManager } = useAuth();
  const [loading, setLoading] = useState(true);
  const [myLeaves, setMyLeaves] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState({});
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [selectedLeaveIds, setSelectedLeaveIds] = useState(new Set());
  const [bulkApproving, setBulkApproving] = useState(false);

  const [formData, setFormData] = useState({
    leave_type: 'casual',
    start_date: null,
    end_date: null,
    reason: '',
    leave_duration: 'full_day',
  });

  // Balance adjustment states
  const [showBalanceDialog, setShowBalanceDialog] = useState(false);
  const [showBalanceImportDialog, setShowBalanceImportDialog] = useState(false);
  const [balanceImportFile, setBalanceImportFile] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState('');
  const [balanceForm, setBalanceForm] = useState({
    earned_leave: 0,
    sick_leave: 0,
    comp_off: 0,
    reason: 'Manual adjustment by HR'
  });
  const [adjusting, setAdjusting] = useState(false);
  const [importingBalances, setImportingBalances] = useState(false);

  const [employeeProfile, setEmployeeProfile] = useState(null);

  // Holiday and Dash board states
  const [holidays, setHolidays] = useState([]);
  const [allBalances, setAllBalances] = useState([]);
  const [showHolidayDialog, setShowHolidayDialog] = useState(false);
  const [holidayForm, setHolidayForm] = useState({
    name: '',
    date: null,
    description: '',
    is_optional: false
  });
  const [holidayLoading, setHolidayLoading] = useState(false);

  const loadLeaveData = useCallback(async () => {
    setLoading(true);
    try {
      const [leaves, balance, empList] = await Promise.all([
        leaveService.getMyLeaves(),
        leaveService.getMyBalance(),
        employeeService.list(),
      ]);
      setMyLeaves(leaves);
      setLeaveBalance(balance);

      const myProfile = empList.find(e => e.user_id === user?.id) || null;
      setEmployeeProfile(myProfile);

      if (isAdmin() || isManager()) {
        const [pending, bulkBalances, holidayList] = await Promise.all([
          leaveService.getPendingApprovals(),
          isAdmin() ? leaveService.getAllBalances() : Promise.resolve([]),
          holidayService.list()
        ]);
        setPendingApprovals(pending);
        setEmployees(empList.filter(e => e.is_active));
        setAllBalances(bulkBalances);
        setHolidays(holidayList);
      } else {
        const holidayList = await holidayService.list();
        setHolidays(holidayList);
      }
    } catch (error) {
      console.error('Failed to load leave data:', error);
      toast.error('Failed to load leave data');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, isManager, user?.id]);

  useEffect(() => {
    loadLeaveData();
  }, [loadLeaveData]);

  const handleApplyLeave = async () => {
    const isHalfDay = formData.leave_duration !== 'full_day';
    if (!formData.start_date || (!isHalfDay && !formData.end_date)) {
      toast.error('Please select both start and end dates');
      return;
    }
    if (!formData.reason.trim()) {
      toast.error('Please provide a reason for leave');
      return;
    }

    const effectiveEndDate = isHalfDay ? formData.start_date : formData.end_date;

    setApplyLoading(true);
    try {
      await leaveService.apply({
        leave_type: formData.leave_type,
        start_date: format(formData.start_date, 'yyyy-MM-dd'),
        end_date: format(effectiveEndDate, 'yyyy-MM-dd'),
        reason: formData.reason,
        leave_duration: formData.leave_duration,
      });

      toast.success('Leave application submitted successfully!');
      setShowApplyDialog(false);
      setFormData({ leave_type: 'casual', start_date: null, end_date: null, reason: '', leave_duration: 'full_day' });
      loadLeaveData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail[0]?.msg : 'Failed to apply leave');
      toast.error(message);
    } finally {
      setApplyLoading(false);
    }
  };

  const toggleLeaveSelect = (id) => {
    setSelectedLeaveIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedLeaveIds.size === pendingApprovals.length) {
      setSelectedLeaveIds(new Set());
    } else {
      setSelectedLeaveIds(new Set(pendingApprovals.map(l => l.id)));
    }
  };

  const handleBulkApprove = async () => {
    if (selectedLeaveIds.size === 0) return;
    setBulkApproving(true);
    let success = 0;
    for (const id of selectedLeaveIds) {
      try {
        await leaveService.approve(id);
        success++;
      } catch (_) {}
    }
    toast.success(`Approved ${success} of ${selectedLeaveIds.size} leave requests`);
    setSelectedLeaveIds(new Set());
    setBulkApproving(false);
    loadData();
  };

  const handleApprove = async (leaveId) => {
    try {
      const role = user?.role;
      if (role === 'manager') {
        await leaveService.managerAction(leaveId, 'approve', '');
      } else {
        // HR/Admin — use hr-action endpoint
        await leaveService.hrAction(leaveId, 'approve', '');
      }
      toast.success('Leave approved successfully!');
      loadLeaveData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail[0]?.msg : 'Failed to approve leave');
      toast.error(message);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    try {
      await leaveService.reject(selectedLeave.id, rejectionReason);
      toast.success('Leave rejected');
      setRejectDialogOpen(false);
      setRejectionReason('');
      setSelectedLeave(null);
      loadLeaveData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail[0]?.msg : 'Failed to reject leave');
      toast.error(message);
    }
  };

  const handleCancelLeave = async (leaveId) => {
    if (!window.confirm('Cancel this leave request?')) return;
    try {
      await leaveService.cancel(leaveId);
      toast.success('Leave cancelled');
      loadLeaveData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel leave');
    }
  };

  const handleAdjustBalance = async () => {
    if (!selectedEmp) {
      toast.error('Please select an employee');
      return;
    }
    setAdjusting(true);
    try {
      await leaveService.updateBalance(selectedEmp, balanceForm);
      toast.success('Leave balance adjusted successfully');
      setShowBalanceDialog(false);
      loadLeaveData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail[0]?.msg : 'Failed to adjust balance');
      toast.error(message);
    } finally {
      setAdjusting(false);
    }
  };

  const handleDownloadBalanceTemplate = () => {
    const toCsvCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const sampleRow = [
      'EMP00001',
      '12',
      '6',
      '2',
      'Opening balance for FY',
    ];
    const csv = [
      LEAVE_BALANCE_IMPORT_COLUMNS.join(','),
      sampleRow.map(toCsvCell).join(','),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'leave_balance_import_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleImportLeaveBalances = async () => {
    if (!balanceImportFile) {
      toast.error('Please select a CSV/XLSX file to upload');
      return;
    }

    setImportingBalances(true);
    try {
      const result = await leaveService.importBalances(balanceImportFile);
      const updatedCount = result?.updated_count || 0;
      const failedCount = result?.failed_count || 0;

      toast.success(`Updated leave balances for ${updatedCount} employee${updatedCount === 1 ? '' : 's'}`);

      if (failedCount > 0) {
        const preview = (result.failed_rows || [])
          .slice(0, 3)
          .map((item) => `Row ${item.row}: ${item.reason}`)
          .join(' | ');
        toast.error(`${failedCount} rows failed${preview ? ` (${preview})` : ''}`);
      }

      setShowBalanceImportDialog(false);
      setBalanceImportFile(null);
      loadLeaveData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : 'Failed to upload leave balances';
      toast.error(message);
    } finally {
      setImportingBalances(false);
    }
  };

  const handleAddHoliday = async () => {
    if (!holidayForm.name || !holidayForm.date) {
      toast.error('Please provide name and date');
      return;
    }
    setHolidayLoading(true);
    try {
      await holidayService.create({
        ...holidayForm,
        date: format(holidayForm.date, 'yyyy-MM-dd')
      });
      toast.success('Holiday published');
      setShowHolidayDialog(false);
      setHolidayForm({ name: '', date: null, description: '', is_optional: false });
      loadLeaveData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail[0]?.msg : 'Failed to publish holiday');
      toast.error(message);
    } finally {
      setHolidayLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: 'Pending', icon: Clock, className: 'badge-warning' },
      approved: { label: 'Approved', icon: CheckCircle2, className: 'badge-success' },
      rejected: { label: 'Rejected', icon: XCircle, className: 'badge-danger' },
      cancelled: { label: 'Cancelled', icon: AlertCircle, className: 'bg-gray-500/20 text-gray-400' },
      manager_approved: { label: 'Manager Approved', icon: CheckCircle2, className: 'bg-amber-500/20 text-amber-600' },
      needs_revision: { label: 'Needs Revision', icon: AlertCircle, className: 'bg-orange-500/20 text-orange-600' },
    };
    const c = config[status] || config.pending;
    return (
      <Badge className={c.className}>
        <c.icon className="w-3 h-3 mr-1" />
        {c.label}
      </Badge>
    );
  };

  const getLeaveTypeColor = (type) => {
    const colors = {
      casual: 'text-blue-400',
      sick: 'text-red-400',
      earned: 'text-green-400',
      loss_of_pay: 'text-gray-400',
      comp_off: 'text-purple-400',
    };
    return colors[type] || 'text-gray-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold tracking-tight">Leave Management</h1>
          <p className="text-muted-foreground mt-1">
            Apply for leaves and track your balance
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin() && (
            <>
              <Dialog
                open={showBalanceImportDialog}
                onOpenChange={(open) => {
                  setShowBalanceImportDialog(open);
                  if (!open) {
                    setBalanceImportFile(null);
                  }
                }}
              >
                <DialogTrigger asChild>
                  <Button variant="outline">
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Balances
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-h-[90dvh] overflow-y-auto max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Upload Leave Balances</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-2">
                    <p className="text-sm text-muted-foreground">
                      Upload a CSV or XLSX file with at least one balance column per row.
                    </p>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" type="button" onClick={handleDownloadBalanceTemplate}>
                        <Download className="w-4 h-4 mr-2" />
                        Download Template
                      </Button>
                      <span className="text-xs text-muted-foreground">Use this format for bulk upload.</span>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="leave-balance-import-file">Import File</Label>
                      <Input
                        id="leave-balance-import-file"
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        onChange={(e) => setBalanceImportFile(e.target.files?.[0] || null)}
                      />
                      {balanceImportFile && (
                        <p className="text-xs text-muted-foreground">
                          Selected: {balanceImportFile.name}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Required: <span className="font-mono">employee_code</span> (or <span className="font-mono">employee_id</span>) and any of <span className="font-mono">earned_leave</span>, <span className="font-mono">sick_leave</span>, <span className="font-mono">comp_off</span>.
                      </p>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowBalanceImportDialog(false);
                        setBalanceImportFile(null);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button onClick={handleImportLeaveBalances} disabled={importingBalances}>
                      {importingBalances && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Upload
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <Dialog open={showBalanceDialog} onOpenChange={setShowBalanceDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-primary text-primary hover:bg-primary/10">
                    <UserCog className="w-4 h-4 mr-2" />
                    Adjust Balance
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-h-[90dvh] overflow-y-auto max-w-md">
                  <DialogHeader>
                    <DialogTitle>Adjust Leave Balance</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Employee</Label>
                      <Select value={selectedEmp} onValueChange={setSelectedEmp}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select employee" />
                        </SelectTrigger>
                        <SelectContent>
                          {employees.map(emp => (
                            <SelectItem key={emp.id} value={emp.id}>
                              {emp.first_name} {emp.last_name} ({emp.employee_code})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Earned</Label>
                        <Input
                          type="number"
                          value={balanceForm.earned_leave}
                          onChange={(e) => setBalanceForm({ ...balanceForm, earned_leave: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Sick</Label>
                        <Input
                          type="number"
                          value={balanceForm.sick_leave}
                          onChange={(e) => setBalanceForm({ ...balanceForm, sick_leave: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Comp</Label>
                        <Input
                          type="number"
                          value={balanceForm.comp_off}
                          onChange={(e) => setBalanceForm({ ...balanceForm, comp_off: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Reason</Label>
                      <Input
                        value={balanceForm.reason}
                        onChange={(e) => setBalanceForm({ ...balanceForm, reason: e.target.value })}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowBalanceDialog(false)}>Cancel</Button>
                    <Button onClick={handleAdjustBalance} disabled={adjusting}>
                      {adjusting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Save Changes
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          )}
          <Dialog open={showApplyDialog} onOpenChange={setShowApplyDialog}>
            <DialogTrigger asChild>
              <Button className="glow-primary" data-testid="apply-leave-btn">
                <Plus className="w-4 h-4 mr-2" />
                Apply Leave
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90dvh] overflow-y-auto max-w-md">
              <DialogHeader>
                <DialogTitle>Apply for Leave</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Leave Type</Label>
                  <Select
                    value={formData.leave_type}
                    onValueChange={(v) => setFormData({ ...formData, leave_type: v })}
                  >
                    <SelectTrigger data-testid="leave-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LEAVE_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Leave Duration</label>
                  <div className="flex gap-3">
                    {[
                      { value: 'full_day', label: 'Full Day' },
                      { value: 'first_half', label: 'First Half' },
                      { value: 'second_half', label: 'Second Half' },
                    ].map(opt => (
                      <label key={opt.value} className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="radio"
                          name="leave_duration"
                          value={opt.value}
                          checked={formData.leave_duration === opt.value}
                          onChange={(e) => setFormData({ ...formData, leave_duration: e.target.value, end_date: e.target.value !== 'full_day' ? formData.start_date : formData.end_date })}
                          className="accent-green-700"
                        />
                        <span className="text-sm text-gray-700">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                          data-testid="start-date-picker"
                        >
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {formData.start_date
                            ? format(formData.start_date, 'MMM dd, yyyy')
                            : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.start_date}
                          onSelect={(date) => setFormData({ ...formData, start_date: date })}
                          disabled={(date) => date < new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {formData.leave_duration === 'full_day' && (
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                          data-testid="end-date-picker"
                        >
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {formData.end_date
                            ? format(formData.end_date, 'MMM dd, yyyy')
                            : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.end_date}
                          onSelect={(date) => setFormData({ ...formData, end_date: date })}
                          disabled={(date) => date < (formData.start_date || new Date())}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  )}
                </div>

                {formData.start_date && formData.end_date && (
                  <div className="p-3 rounded-lg bg-secondary/50 text-sm">
                    <span className="text-muted-foreground">Total Days: </span>
                    <span className="font-semibold">
                      {differenceInDays(formData.end_date, formData.start_date) + 1}
                    </span>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Reason</Label>
                  <Textarea
                    placeholder="Enter reason for leave..."
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    rows={3}
                    data-testid="leave-reason"
                  />
                </div>
              </div>
              {formData.start_date && (
                <p className="text-xs text-gray-500 bg-amber-50 border border-amber-200 rounded p-2">
                  💡 Deduction: <strong>{formData.leave_duration === 'full_day' ? '1.0 day' : '0.5 day'}</strong> from your {formData.leave_type} balance
                </p>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowApplyDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleApplyLeave} disabled={applyLoading} data-testid="submit-leave-btn">
                  {applyLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Submit Application
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Leave Balance Cards */}
      {leaveBalance.no_leave_policy ? (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6 pb-4">
            <p className="text-sm font-medium text-amber-800">No Leave Policy Applicable</p>
            <p className="text-xs text-amber-700 mt-1">
              Leave balance is only applicable for permanent employees. Your employment type is{' '}
              <span className="capitalize font-semibold">{leaveBalance.employee_type}</span>.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-4" data-testid="leave-balance-grid">
          {Object.entries(leaveBalance).map(([type, data]) => (
            <Card key={type} className="card-hover">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-2">
                  <span className={`font-medium capitalize ${getLeaveTypeColor(type)}`}>
                    {type.replace('_', ' ')}
                  </span>
                </div>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-3xl font-bold">{data.available}</span>
                  <span className="text-sm text-muted-foreground">/ {data.total}</span>
                </div>
                <Progress
                  value={(data.available / data.total) * 100}
                  className="h-2"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  {data.used} days used
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Tabs defaultValue="my-leaves" className="w-full">
        <TabsList>
          <TabsTrigger value="my-leaves" data-testid="tab-my-leaves">My Leaves</TabsTrigger>
          {(isAdmin() || isManager()) && (
            <TabsTrigger value="approvals" data-testid="tab-approvals">
              Pending Approvals
              {pendingApprovals.length > 0 && (
                <Badge className="ml-2 bg-primary">{pendingApprovals.length}</Badge>
              )}
            </TabsTrigger>
          )}
          {isAdmin() && (
            <TabsTrigger value="dashboard">Leave Dashboard</TabsTrigger>
          )}
          {employeeProfile?.location_type !== 'store' && (
            <TabsTrigger value="holidays">Holiday List</TabsTrigger>
          )}
        </TabsList>

        {/* My Leaves Tab */}
        <TabsContent value="my-leaves" className="space-y-4">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto"><Table data-testid="my-leaves-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Leave Type</TableHead>
                    <TableHead className="hidden sm:table-cell">Start Date</TableHead>
                    <TableHead className="hidden sm:table-cell">End Date</TableHead>
                    <TableHead>Days</TableHead>
                    <TableHead className="hidden md:table-cell">Reason</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {myLeaves.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        No leave applications found
                      </TableCell>
                    </TableRow>
                  ) : (
                    myLeaves.map((leave) => (
                      <TableRow key={leave.id}>
                        <TableCell>
                          <span className={`font-medium capitalize ${getLeaveTypeColor(leave.leave_type)}`}>
                            {leave.leave_type.replace('_', ' ')}
                          </span>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">{format(new Date(leave.start_date), 'MMM dd, yyyy')}</TableCell>
                        <TableCell className="hidden sm:table-cell">{format(new Date(leave.end_date), 'MMM dd, yyyy')}</TableCell>
                        <TableCell>{leave.days}</TableCell>
                        <TableCell className="hidden md:table-cell max-w-xs truncate">{leave.reason}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 flex-wrap">
                            {getStatusBadge(leave.status)}
                            {(leave.status === 'pending' || leave.status === 'manager_approved') && (
                              <button
                                onClick={() => handleCancelLeave(leave.id)}
                                className="text-xs text-red-600 hover:text-red-800 border border-red-200 rounded px-2 py-0.5"
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Approvals Tab */}
        {(isAdmin() || isManager()) && (
          <TabsContent value="approvals" className="space-y-4">
            {selectedLeaveIds.size > 0 && (
              <div className="flex items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                <span className="text-sm font-medium">{selectedLeaveIds.size} selected</span>
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                  onClick={handleBulkApprove}
                  disabled={bulkApproving}
                >
                  {bulkApproving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  Approve Selected
                </Button>
                <Button size="sm" variant="outline" onClick={() => setSelectedLeaveIds(new Set())}>
                  Clear
                </Button>
              </div>
            )}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto"><Table data-testid="pending-approvals-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded"
                          checked={pendingApprovals.length > 0 && selectedLeaveIds.size === pendingApprovals.length}
                          onChange={toggleSelectAll}
                        />
                      </TableHead>
                      <TableHead>Employee</TableHead>
                      <TableHead className="hidden md:table-cell">Department</TableHead>
                      <TableHead className="hidden sm:table-cell">Leave Type</TableHead>
                      <TableHead className="hidden sm:table-cell">Dates</TableHead>
                      <TableHead>Days</TableHead>
                      <TableHead className="hidden lg:table-cell">Reason</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingApprovals.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          <CheckCircle2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          No pending approvals
                        </TableCell>
                      </TableRow>
                    ) : (
                      pendingApprovals.map((leave) => (
                        <TableRow key={leave.id} className={selectedLeaveIds.has(leave.id) ? 'bg-primary/5' : ''}>
                          <TableCell>
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded"
                              checked={selectedLeaveIds.has(leave.id)}
                              onChange={() => toggleLeaveSelect(leave.id)}
                            />
                          </TableCell>
                          <TableCell className="font-medium">{leave.employee_name}</TableCell>
                          <TableCell className="hidden md:table-cell">{leave.department}</TableCell>
                          <TableCell className="hidden sm:table-cell">
                            <span className={`capitalize ${getLeaveTypeColor(leave.leave_type)}`}>
                              {leave.leave_type.replace('_', ' ')}
                            </span>
                          </TableCell>
                          <TableCell className="hidden sm:table-cell">
                            <div className="text-sm">
                              {format(new Date(leave.start_date), 'MMM dd')}
                              <ChevronRight className="w-3 h-3 inline mx-1" />
                              {format(new Date(leave.end_date), 'MMM dd')}
                            </div>
                          </TableCell>
                          <TableCell>{leave.days}</TableCell>
                          <TableCell className="hidden lg:table-cell max-w-xs truncate">{leave.reason}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleApprove(leave.id)}
                                className="bg-green-600 hover:bg-green-700"
                                data-testid={`approve-leave-${leave.id}`}
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => {
                                  setSelectedLeave(leave);
                                  setRejectDialogOpen(true);
                                }}
                                data-testid={`reject-leave-${leave.id}`}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {isAdmin() && (
          <TabsContent value="dashboard">
            <Card>
              <CardHeader>
                <CardTitle>Employee Leave Balances</CardTitle>
                <CardDescription>View current leave balances for all active employees</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead className="hidden md:table-cell">Department</TableHead>
                      <TableHead>Earned</TableHead>
                      <TableHead className="hidden sm:table-cell">Sick</TableHead>
                      <TableHead className="hidden sm:table-cell">Comp Off</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allBalances.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                          No balance data available
                        </TableCell>
                      </TableRow>
                    ) : (
                      allBalances.map((item) => (
                        <TableRow key={item.employee_id}>
                          <TableCell>
                            <div className="font-medium">{item.employee_name}</div>
                            <div className="text-xs text-muted-foreground">{item.employee_code}</div>
                          </TableCell>
                          <TableCell className="hidden md:table-cell">{item.department}</TableCell>
                          <TableCell>
                            {item.balances.earned.used} / {item.balances.earned.total}
                          </TableCell>
                          <TableCell className="hidden sm:table-cell">
                            {item.balances.sick.used} / {item.balances.sick.total}
                          </TableCell>
                          <TableCell className="hidden sm:table-cell">
                            {item.balances.comp_off.used} / {item.balances.comp_off.total}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {employeeProfile?.location_type !== 'store' && (
        <TabsContent value="holidays">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Holiday List {new Date().getFullYear()}</CardTitle>
                <CardDescription>Official holidays for the current year</CardDescription>
              </div>
              {isAdmin() && (
                <Dialog open={showHolidayDialog} onOpenChange={setShowHolidayDialog}>
                  <DialogTrigger asChild>
                    <Button size="sm">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Holiday
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg max-h-[90dvh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Add New Holiday</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Holiday Name</Label>
                        <Input
                          value={holidayForm.name}
                          onChange={(e) => setHolidayForm({ ...holidayForm, name: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Date</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-full justify-start text-left font-normal"
                            >
                              <CalendarIcon className="w-4 h-4 mr-2" />
                              {holidayForm.date ? format(holidayForm.date, 'MMM dd, yyyy') : 'Select date'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={holidayForm.date}
                              onSelect={(date) => setHolidayForm({ ...holidayForm, date })}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Input
                          value={holidayForm.description}
                          onChange={(e) => setHolidayForm({ ...holidayForm, description: e.target.value })}
                        />
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="optional"
                          checked={holidayForm.is_optional}
                          onChange={(e) => setHolidayForm({ ...holidayForm, is_optional: e.target.checked })}
                        />
                        <Label htmlFor="optional">Optional Holiday</Label>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowHolidayDialog(false)}>Cancel</Button>
                      <Button onClick={handleAddHoliday} disabled={holidayLoading}>
                        {holidayLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        Publish
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Holiday Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="hidden sm:table-cell">Description</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {holidays.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                        No holidays published yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    holidays.map((holiday) => (
                      <TableRow key={holiday.id}>
                        <TableCell className="font-medium">
                          {format(new Date(holiday.date), 'MMM dd, yyyy')}
                        </TableCell>
                        <TableCell>{holiday.name}</TableCell>
                        <TableCell>
                          <Badge variant={holiday.is_optional ? "outline" : "default"}>
                            {holiday.is_optional ? "Optional" : "Compulsory"}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell text-muted-foreground">{holiday.description || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}
      </Tabs>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Reject Leave Application</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Reason for Rejection</Label>
              <Textarea
                placeholder="Enter reason for rejection..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={3}
                data-testid="rejection-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleReject} data-testid="confirm-reject-btn">
              Reject Leave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
